import React, { useMemo, useRef, useState } from 'react';
import './DropdownInput.scss';
import PropTypes from 'prop-types';

import ChevronDown from '../../assets/icons/chevron_bottom.svg';

export default function DropdownInput(props) {
  const {
    value,
    valueChange,
    validateValue,
    placeholder,
    visible,
    icon,
    errorMessage,
    enterPressed,
    dropdownList,
    idKey,
    displayedKey,
  } = props;

  const inputRef = useRef(null);
  const [error, setError] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const processedDropdownList = useMemo(() => dropdownList.map((item) => {
    const normalizedValue = item[displayedKey].toLowerCase().replace(/[^a-zA-Z0-9 ]/g, '');
    const words = normalizedValue.split(' ').map((_, i, arr) => arr.slice(i).join(''));

    return {
      ...item,
      normalizedValue,
      words,
    };
  }), [dropdownList, displayedKey]);

  const displayedItems = useMemo(() => {
    const normalizedSearch = value.toLowerCase().replace(/[^a-zA-Z0-9]/g, '');

    return processedDropdownList.filter(
      ({ words }) => words.some((word) => word.startsWith(normalizedSearch)),
    );
  }, [value, processedDropdownList]);

  const haveItemsToDisplay = displayedItems && displayedItems.length > 0;

  function checkError(newValue) {
    const valueError = !dropdownList.find(
      (item) => item[displayedKey].toLowerCase() === newValue.toLowerCase(),
    );

    setError(valueError);

    return valueError;
  }

  function checkErrorAndValidate(newValue) {
    checkError(newValue);

    validateValue(newValue);

    setTimeout(() => {
      setDropdownVisible(false);
    }, 50);
  }

  function handleFocusInput() {
    setDropdownVisible(true);
    setError(false);
  }

  function handleItemSelection(item) {
    valueChange(item);

    setError(false);
    setDropdownVisible(false);

    setTimeout(() => {
      inputRef.current.blur();
    }, 200);
  }

  function handleValueChange(newValue) {
    setError(false);
    valueChange(newValue);
  }

  function handleEnterPressed(element) {
    element.blur();

    if (!checkError(value)) enterPressed();
  }

  return (
    <div className={`input-container with-dropdown ${!visible && 'hidden'} ${error && 'errored'}`}>
      { icon }

      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={value}
        onBlur={(e) => checkErrorAndValidate(e.target.value)}
        onChange={(e) => handleValueChange(e.target.value)}
        onFocus={() => handleFocusInput()}
        onKeyDown={(e) => e.key === 'Enter' && handleEnterPressed(e.target)}
      />

      { dropdownVisible && haveItemsToDisplay && (
        <div className="choices-container" onMouseDown={(e) => e.preventDefault()}>
          <div className="items-container" onMouseDown={(e) => e.preventDefault()}>
            {displayedItems && displayedItems.map((item) => (
              <button
                key={item[idKey]}
                type="button"
                onMouseDown={() => handleItemSelection(item[displayedKey])}
                tabIndex={0}
              >
                <p>{item[displayedKey]}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {error && <p className="error-message">{errorMessage}</p>}

      <ChevronDown
        width="18px"
        height="18px"
        stroke="#2A195A"
        className={(dropdownVisible && haveItemsToDisplay) ? 'rotated' : ''}
      />
    </div>
  );
}

DropdownInput.defaultProps = {
  value: '',
  valueChange: () => {},
  validateValue: () => {},
  placeholder: '',
  visible: true,
  icon: undefined,
  errorMessage: undefined,
  enterPressed: () => {},
  dropdownList: undefined,
};

DropdownInput.propTypes = {
  value: PropTypes.string,
  valueChange: PropTypes.func,
  validateValue: PropTypes.func,
  placeholder: PropTypes.string,
  visible: PropTypes.bool,
  icon: PropTypes.element,
  errorMessage: PropTypes.string,
  enterPressed: PropTypes.func,
  dropdownList: PropTypes.arrayOf(PropTypes.shape({})),
  idKey: PropTypes.string.isRequired,
  displayedKey: PropTypes.string.isRequired,
};
